import { useEffect, useState } from "react";
import axios from "axios";
import { ChartBar, LinkSimple, Warning } from "@phosphor-icons/react";
import { fmtCurrency } from "@/lib/format";
import { PRESET_BY_ID } from "@/lib/presets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadAnalytics = ({ version = 0 }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/leads/analytics`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [version]);

  if (!data || data.total === 0) return null;

  return (
    <div data-testid="leads-analytics" className="mb-8">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-4">
        <h2 className="font-serif text-2xl text-brand-forest">What converts best</h2>
        <span className="text-xs text-brand-muted">
          {data.total} lead{data.total === 1 ? "" : "s"} total · {data.last_30_days} in the last 30 days
        </span>
        <span
          data-testid="analytics-conversion"
          className="text-xs font-semibold text-brand-forest bg-brand-forest/10 px-2 py-0.5 rounded"
        >
          {data.clients || 0} client{(data.clients || 0) === 1 ? "" : "s"} ·{" "}
          {Math.round((data.conversion_rate || 0) * 100)}% converted
        </span>
        {data.revenue > 0 && (
          <span
            data-testid="analytics-revenue"
            className="text-xs font-semibold text-brand-copper bg-brand-copper/10 px-2 py-0.5 rounded"
          >
            {fmtCurrency(data.revenue)} annual premium
          </span>
        )}
      </div>

      {data.goal?.target > 0 && (
        <div
          data-testid="analytics-goal"
          className="mb-5 bg-brand-forest text-brand-cream rounded-md p-5"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-cream/60">
              {data.goal.year} premium goal
            </div>
            <div className="text-xs text-brand-cream/70 tabular-nums" data-testid="goal-figures">
              {fmtCurrency(data.goal.premium_ytd)} of {fmtCurrency(data.goal.target)} ·{" "}
              <span className="font-semibold text-brand-cream">
                {Math.round((data.goal.pct || 0) * 100)}%
              </span>
            </div>
          </div>
          <div className="mt-3 h-3 rounded-full bg-brand-cream/15 overflow-hidden">
            <div
              data-testid="goal-progress-bar"
              className="h-full rounded-full bg-brand-copper transition-all duration-700"
              style={{ width: `${Math.max((data.goal.pct || 0) * 100, 1.5)}%` }}
            />
          </div>
          <div className="mt-2 text-[11px] text-brand-cream/60">
            {data.goal.premium_ytd >= data.goal.target
              ? "Goal reached — time to raise the bar."
              : `${fmtCurrency(data.goal.target - data.goal.premium_ytd)} of annual premium left to write this year.`}
          </div>
        </div>
      )}

      {data.pipeline && (
        <div
          data-testid="analytics-pipeline"
          className="mb-5 bg-white border border-brand-line rounded-md p-5 grid grid-cols-2 sm:grid-cols-5 gap-4"
        >
          {data.pipeline.map((p) => (
            <div key={p.stage} data-testid={`pipeline-${p.stage}`}>
              <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted">{p.label}</div>
              <div
                className={`mt-1 font-serif text-2xl ${
                  p.stage === "client" ? "text-brand-copper" : p.stage === "lost" ? "text-brand-muted" : "text-brand-forest"
                }`}
              >
                {p.count}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-5">
        {data.stalled?.some((s) => s.open > 0 || s.lost_from > 0) && (
          <div
            data-testid="analytics-stalled"
            className="md:col-span-2 bg-white border border-brand-line rounded-md p-5"
          >
            <div className="flex items-center gap-2 mb-1">
              <Warning size={16} weight="duotone" className="text-brand-copper" />
              <h3 className="font-serif text-lg text-brand-forest">Where leads drop off</h3>
            </div>
            <p className="text-xs text-brand-muted mb-4">
              Idle means no stage change or call note for 7+ days. Lost here is where each "not a fit" was
              sitting when you closed it.
            </p>
            <div className="space-y-3">
              {data.stalled.map((s) => {
                const worst = Math.max(...data.stalled.map((x) => x.lost_from), 1);
                return (
                  <div key={s.stage} data-testid={`stalled-${s.stage}`} className="sm:flex sm:items-center gap-4">
                    <div className="sm:w-32 text-sm font-medium text-brand-ink/85">{s.label}</div>
                    <div className="flex-1 mt-1 sm:mt-0">
                      <div className="h-2 rounded-full bg-brand-cream overflow-hidden">
                        <div
                          className="h-full rounded-full bg-brand-copper transition-all"
                          style={{ width: `${(s.lost_from / worst) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="mt-1 sm:mt-0 text-xs tabular-nums text-brand-muted sm:w-56 sm:text-right">
                      <span className="text-brand-forest font-semibold">{s.open}</span> open ·{" "}
                      <span className={s.stalled > 0 ? "text-brand-copper font-semibold" : ""}>
                        {s.stalled} idle
                      </span>{" "}
                      · <span className="font-semibold">{s.lost_from}</span> lost here
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <BarCard
          icon={ChartBar}
          title="Top industries"
          testId="analytics-industries"
          rows={data.by_industry.slice(0, 6).map((d) => ({
            key: d.industry,
            label: PRESET_BY_ID[d.industry]?.label || d.industry,
            count: d.count,
            won: d.won || 0,
            sub: d.revenue
              ? `${fmtCurrency(d.revenue, { compact: true })} premium`
              : d.avg_teaser
              ? `avg ${fmtCurrency(d.avg_teaser, { compact: true })} teaser`
              : "",
          }))}
        />
        <BarCard
          icon={LinkSimple}
          title="Top referral links"
          testId="analytics-refs"
          rows={data.by_ref.slice(0, 6).map((d) => ({
            key: d.ref,
            label: d.ref === "direct" ? "Direct (no ref)" : `?ref=${d.ref}`,
            count: d.count,
            won: d.won || 0,
            sub: d.revenue ? `${fmtCurrency(d.revenue, { compact: true })} premium` : "",
          }))}
        />
      </div>
    </div>
  );
};

function BarCard({ icon: Icon, title, rows, testId }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div data-testid={testId} className="bg-white border border-brand-line rounded-md p-6">
      <div className="flex items-center gap-2 mb-5">
        <Icon size={18} weight="duotone" className="text-brand-copper" />
        <span className="text-[11px] uppercase tracking-[0.16em] text-brand-muted font-semibold">{title}</span>
      </div>
      <div className="space-y-3.5">
        {rows.map((r) => (
          <div key={r.key} data-testid={`${testId}-row-${r.key}`}>
            <div className="flex items-baseline justify-between text-sm mb-1">
              <span className="text-brand-ink/85 font-medium">{r.label}</span>
              <span className="tabular-nums text-brand-forest font-semibold">
                {r.won > 0 && (
                  <span className="mr-2 text-[11px] text-brand-copper font-semibold">
                    {r.won} won
                  </span>
                )}
                {r.count}
                {r.sub && <span className="ml-2 text-[11px] text-brand-muted font-normal">{r.sub}</span>}
              </span>
            </div>
            <div className="h-2 rounded-full bg-brand-cream overflow-hidden relative">
              <div
                className="h-full rounded-full bg-brand-forest transition-all"
                style={{ width: `${Math.max((r.count / max) * 100, 6)}%` }}
              />
              {r.won > 0 && (
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-brand-copper transition-all"
                  style={{ width: `${Math.max((r.won / max) * 100, 4)}%` }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
